import React from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import 'moment/locale/sv';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { useI18n, useSelector } from '~Hooks';
import DeleteIcon from '~/Assets/icons/delete.svg';

const OuterWrapper = styled.div``;

const Wrapper = styled.div`
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid #fbfbfb;
  overflow: hidden;
  padding-bottom: 16px;
  width: 368px;
  margin-right: 48px;
  margin-bottom: 16px;
`;

const InfoHeader = styled.div`
  padding: 20px 16px;
  border-bottom: 1px solid #e8e9ea;
  background: #fbfbfb;
  position: relative;
`;

const Header = styled.div`
  color: '#1c232c';
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.36px;
`;

const Stats = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  font-weight: 600;
  letter-spacing: 0.36px;
  padding: 8px 0px;
  border-bottom: 1px solid rgb(232, 233, 234);
  font-size: 14px;
  margin: 0 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const Text = styled.div`
  color: #0e1216;
`;

const Comment = styled.div`
  color: #0e1216;
  margin: 8px 16px 0;
  font-weight: 600;
  letter-spacing: 0.36px;
  font-size: 14px;

  p {
    font-size: 14px;
    margin-top: 8px;
    background-color: #eeeef1;
    border-radius: 4px;
    padding: 8px 8px 10px;
    color: #0e1216;
    line-height: 14px;
  }
`;

const DeleteWrapper = styled.div`
  position: absolute;
  right: 16px;
  padding: 8px;
  border-radius: 4px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  background-color: #eeeef1;
`;

const Delete = styled.div`
  mask-image: url(${DeleteIcon});
  height: 24px;
  width: 24px;
  mask-size: contain;
  background-color: #757578;
`;

interface Props {
  venueItems: { id: string; title: string }[];
}

const OrdersList: React.FC<Props> = ({ venueItems }) => {
  const [, , venueId] = location.hash.split('/');
  const { i18n } = useI18n();
  const universe = useInventoryUniverse();
  const api = useInventoryApi(venueId);
  const sortedRequests =
    universe?.requests?.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    ) ?? [];

  const handleDelete = (id: string) => {
    api.removeRequest(id);
  };

  return (
    <OuterWrapper>
      {sortedRequests.map((request) => (
        <Wrapper key={request.id}>
          <InfoHeader>
            <Header>{moment(request.timestamp).format('MMMM Do YYYY')}</Header>
            <DeleteWrapper onClick={() => handleDelete(request.id)}>
              <Delete />
            </DeleteWrapper>
          </InfoHeader>
          {request.items.map((item) => {
            const matchingItem = venueItems.find((i: { id: string }) => i.id === item.itemId);
            return (
              <Stats key={item.itemId}>
                <Text>{matchingItem?.title ?? '-'}</Text>
                <Text>{item.count}</Text>
              </Stats>
            );
          })}
          {request.comment && (
            <Comment>
              <Text>{i18n('Inventory.OrderComment')}</Text>
              <p>{request.comment}</p>
            </Comment>
          )}
        </Wrapper>
      ))}
    </OuterWrapper>
  );
};

export default OrdersList;
